/**
 * Saved and shared plans.
 *
 * A shared plan travels in the URL and must contain nothing sensitive: no OAuth
 * tokens, no Bungie membership ids, and no item instance ids. It is keyed by
 * stable manifest hashes rather than names so it survives localization and
 * cosmetic renames.
 */
import { z } from "zod";
import { DATA_VERSION, SOCKET_ORDER } from "./types.js";
import type { CraftingSocketKey, PlannerMode, WeaponConfiguration } from "./types.js";

const configurationSchema = z
  .object({
    intrinsic: z.number().int().positive().optional(),
    barrel: z.number().int().positive().optional(),
    magazine: z.number().int().positive().optional(),
    traitOne: z.number().int().positive().optional(),
    traitTwo: z.number().int().positive().optional(),
    origin: z.number().int().positive().optional(),
    memento: z.number().int().positive().optional(),
  })
  .strict();

export const sharedPlanSchema = z
  .object({
    v: z.number().int().positive(),
    /** Recipe hash of the weapon. */
    w: z.number().int().positive(),
    mode: z.enum(["new-shape", "owned-reshape"]),
    /** Planned configuration. */
    p: configurationSchema,
    /** Base configuration — the initial shape, or the owned weapon's real state. */
    b: configurationSchema.optional(),
    /** Level and percent, included only when the user opts in. */
    lvl: z.number().int().min(0).max(99).optional(),
    pct: z.number().min(0).max(99.99).optional(),
  })
  .strict();

export type SharedPlan = z.infer<typeof sharedPlanSchema>;

export interface PlanInput {
  recipeHash: number;
  mode: PlannerMode;
  plannedConfiguration: WeaponConfiguration;
  baseConfiguration?: WeaponConfiguration;
  /** Only set when the user chose to include their progress. */
  currentWeaponLevel?: number;
  currentLevelPercent?: number;
}

/** Drop undefined keys so the encoded form stays short and stable. */
function compact(config: WeaponConfiguration): WeaponConfiguration {
  const out: WeaponConfiguration = {};
  for (const key of SOCKET_ORDER) {
    const value = config[key];
    if (typeof value === "number" && Number.isInteger(value) && value > 0) out[key] = value;
  }
  return out;
}

export function buildSharedPlan(input: PlanInput): SharedPlan {
  const plan: SharedPlan = {
    v: DATA_VERSION,
    w: input.recipeHash,
    mode: input.mode,
    p: compact(input.plannedConfiguration),
  };
  if (input.baseConfiguration) plan.b = compact(input.baseConfiguration);
  if (typeof input.currentWeaponLevel === "number") plan.lvl = Math.trunc(input.currentWeaponLevel);
  if (typeof input.currentLevelPercent === "number") plan.pct = input.currentLevelPercent;
  return plan;
}

// --- base64url codec ---------------------------------------------------------

function toBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(value: string): Uint8Array {
  const padded = value.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function encodePlan(plan: SharedPlan): string {
  return toBase64Url(new TextEncoder().encode(JSON.stringify(plan)));
}

export type DecodeResult =
  | { ok: true; plan: SharedPlan; outdated: boolean }
  | { ok: false; error: "malformed" | "invalid" };

/**
 * Decode a shared plan. Never throws — a bad link is a normal thing to receive,
 * and the UI shows a recoverable error state for it. `outdated` means the plan
 * was written against an older data version and may reference removed plugs.
 */
export function decodePlan(encoded: string): DecodeResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(fromBase64Url(encoded)));
  } catch {
    return { ok: false, error: "malformed" };
  }
  const result = sharedPlanSchema.safeParse(parsed);
  if (!result.success) return { ok: false, error: "invalid" };
  return { ok: true, plan: result.data, outdated: result.data.v !== DATA_VERSION };
}

export function planToUrl(plan: SharedPlan, baseUrl: string): string {
  const url = new URL(`/plan/${encodePlan(plan)}`, baseUrl);
  return url.toString();
}

// --- local persistence -------------------------------------------------------

/** A stored plan is a shared plan plus local bookkeeping. It gets its own
 *  schema because `sharedPlanSchema` is strict — the metadata below is allowed
 *  in local storage but must never appear in a shared URL. */
export const savedPlanSchema = sharedPlanSchema.extend({
  id: z.string().min(1),
  name: z.string(),
  savedAt: z.string(),
  /**
   * The crafted weapon this plan was built against, so reopening it can show
   * the weapon's *current* level rather than the level it had when saved.
   *
   * Local storage only. It is deliberately absent from `sharedPlanSchema`,
   * which is `.strict()` — an item instance id is a private identifier and must
   * never reach a shared link.
   */
  instanceId: z.string().optional(),
});

export type SavedPlan = z.infer<typeof savedPlanSchema>;

const STORAGE_KEY = "d2crafting.savedPlans";

export function loadSavedPlans(storage: Storage): SavedPlan[] {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Validate each entry independently so one corrupt plan cannot wipe the list.
    return parsed.flatMap((entry) => {
      const result = savedPlanSchema.safeParse(entry);
      return result.success ? [result.data] : [];
    });
  } catch {
    return [];
  }
}

export function savePlan(
  storage: Storage,
  plan: SharedPlan,
  name: string,
  instanceId?: string
): SavedPlan {
  const saved: SavedPlan = {
    ...plan,
    id: crypto.randomUUID(),
    name: name.trim() || "Untitled plan",
    savedAt: new Date().toISOString(),
    ...(instanceId ? { instanceId } : {}),
  };
  const all = [saved, ...loadSavedPlans(storage)];
  storage.setItem(STORAGE_KEY, JSON.stringify(all));
  return saved;
}

export function deleteSavedPlan(storage: Storage, id: string): void {
  storage.setItem(STORAGE_KEY, JSON.stringify(loadSavedPlans(storage).filter((p) => p.id !== id)));
}

/** Keys present in a configuration, for "N components planned" style summaries. */
export function configuredKeys(config: WeaponConfiguration): CraftingSocketKey[] {
  return SOCKET_ORDER.filter((key) => config[key] !== undefined);
}
